import React, { useEffect, useState } from "react";
import { useWatch, useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import InputPhone from "@src/components/InputPhone";
import Button from "@src/components/Button";
import cashGift from "../cashGift.module.scss";
import toast from "react-hot-toast";
import {
  GenerateGiftPaymentMetadata,
  VerifyTransaction,
} from "@src/graphql/mutations";
import {
  Currency,
  EventSupportCurrencyProvider,
  GenerateGiftPaymentMetadataMutation,
  GenerateGiftPaymentMetadataMutationVariables,
  GiftPaymentMetadataRequest,
  PaymentProcessor,
  UserQuery,
  VerifyTransactionMutation,
  VerifyTransactionMutationVariables,
} from "@src/@types/api.d";
import { useMutation, useQuery } from "@apollo/client";
import Icon from "@src/components/Icon";
import { GetProfileDetails } from "@src/graphql/queries";
import { useFlutterwave } from "flutterwave-react-v3";
import { FlutterwaveConfig } from "flutterwave-react-v3/dist/types";
import getSymbolFromCurrency from "currency-symbol-map";
import { formatMoney } from "@src/utils/general";
import useStore from "@src/store";

interface Values {
  fullName: string;
  email: string;
  amount: string | number;
}

const GeneralFormSchema = Yup.object().shape({
  fullName: Yup.string().required("Full name is required"),
  email: Yup.string().email().required("Email is required"),
  amount: Yup.string().required("An amount is required"),
});

interface CashGiftModalProps {
  eventId: number;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  eventUuid: string | undefined;
  provider: EventSupportCurrencyProvider | null;
  currencyToPay: Currency | null;
}

const CashGiftPayment = ({
  eventId,
  setOpen,
  eventUuid,
  provider,
  currencyToPay,
}: CashGiftModalProps) => {
  const isPreviewMode = useStore((state) => state.isPreviewMode);

  const { data: profile } = useQuery<UserQuery>(GetProfileDetails, {
    onError(error) {
      if (error.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      }
    },
  });

  const [defaultValues, setDefaultValues] = useState<Values>({
    fullName: "",
    email: "",
    amount: "",
  });

  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    formState: { errors, isDirty, isValid, isSubmitting },
  } = useForm<Values>({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(GeneralFormSchema),
  });

  useEffect(() => {
    if (profile?.user) {
      const newDefaultValues = {
        fullName: `${profile.user.first_name || ""} ${
          profile.user.last_name || ""
        }`.trim(),
        email: profile.user.email || "",
        amount: "",
      };
      setDefaultValues(newDefaultValues);

      reset(newDefaultValues); // Reset form with new default values
    }
  }, [profile, reset]);

  const amount = useWatch({ control, name: "amount" });
  const [phoneNumberValue, setPhoneNumberValue] = React.useState<string>("");

  useEffect(() => {
    setPhoneNumberValue(profile?.user?.phone_number || "");
  }, [profile?.user?.phone_number]);

  const [verifyTransaction] = useMutation<
    VerifyTransactionMutation,
    VerifyTransactionMutationVariables
  >(VerifyTransaction, {
    onError(error) {
      // console.log(error);
      if (error.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      }
    },
  });

  // let monnifyConfigData = {
  //   apiKey: process.env.NEXT_PUBLIC_MONNIFY_API_ENV!,
  //   reference: new Date().getTime().toString(),
  //   amount: Number(amount),
  //   currency: "NGN",
  //   paymentMethods: ["CARD", "ACCOUNT_TRANSFER"],
  //   customerFullName: fullName,
  //   customerEmail: email,
  //   contractCode: process.env.NEXT_PUBLIC_MONNIFY_CONTRACT_CODE!,
  //   customerMobileNumber: "phoneNumberValue",
  //   isTestMode: true,
  //   metadata: {
  //     title: "Cash Gift",
  //     variable_name: "cash_gift",
  //     event_id: eventId,
  //     trans_type: "credit",
  //     user_id: userId,
  //     description: "paid for cash gift",
  //     logo: "",
  //   },
  // };

  // let initializePaymentWithMonnify = useMonnifyPayment(monnifyConfigData);

  // const onSuccessWithMonnify = (reference: any) => {
  //   if (reference.paymentStatus === "PAID") {
  //     PayForCashGiftFn({
  //       variables: {
  //         payload: {
  //           currency: Currency.ngn,
  //           eventID: eventId,
  //           amount: Number(amount),
  //           status: true,
  //           fullName: fullName,
  //           phone: phoneNumberValue,
  //           email: email,
  //           reference: reference.transactionReference as string,
  //           paymentProcessor: paymentProcessor.monnify,
  //           creditorAlias: fullName,
  //         },
  //       },
  //     });
  //     toast.success(<p className="toast">Payment successful</p>);
  //   }
  // };

  // Mutation for generating payment metadata
  const [generateGiftMetadataFn] = useMutation<
    GenerateGiftPaymentMetadataMutation,
    GenerateGiftPaymentMetadataMutationVariables
  >(GenerateGiftPaymentMetadata, {
    onError(error) {
      if (error.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      }
    },
  });

  const [flutterwaveConfig, setFlutterwaveConfig] =
    useState<FlutterwaveConfig | null>(null);

  const onSuccessWithPaystack = (reference: string) => {
    verifyTransaction({
      variables: {
        reference,
        provider: PaymentProcessor.Paystack,
      },
    });
    toast.success(<p className="toast">Payment successful</p>);
  };

  const paystackCharge = React.useMemo(() => {
    const preTotal = parseInt(`${amount}`);
    const percentCharge = 0.015 * preTotal;
    const fullCharge = percentCharge + 100;
    if (preTotal < 2500) {
      return { charge: percentCharge };
    } else if (fullCharge > 2000) {
      return { charge: 2000, with100: true };
    } else {
      return { charge: fullCharge, with100: true };
    }
  }, [amount]);

  const finalPaystackAmount = React.useMemo(() => {
    const paystackFeeRate = 0.015;
    let paystackFixedFee = 0;
    const feeCap = 2000;
    if (Number(amount) >= 2500) {
      paystackFixedFee = 100.0;
    }
    const applicableFee = paystackFeeRate * Number(amount) + paystackFixedFee;
    const final =
      applicableFee > feeCap
        ? Number(amount) + feeCap
        : (Number(amount) + paystackFixedFee) / (1 - paystackFeeRate) + 0.01;
    const res = parseFloat(final.toFixed(2));
    return res;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, paystackCharge?.charge]);

  const payWithPaystack: SubmitHandler<Values> = async (data) => {
    if (!eventUuid) {
      toast.error("Event ID is missing");
      return;
    }

    const input: GiftPaymentMetadataRequest = {
      amount: Number(data.amount) * 100,
      contact_email: data.email,
      contact_phone: phoneNumberValue,
      currency: Currency.Ngn,
      currency_iso: Currency.Ngn,
      event_id: eventUuid ?? "",
      full_name: data.fullName,
      note: "Cash gift payment",
      payment_provider: PaymentProcessor.Paystack,
      // user_id: profile?.user?.uuid ?? "",
    };

    try {
      // Call mutation to generate payment metadata
      const { data: mutationData } = await generateGiftMetadataFn({
        variables: {
          input,
        },
      });

      const paymentMeta = mutationData?.generateGiftPaymentMetadata;
      const reference = paymentMeta?.payment_reference || "";

      if (!paymentMeta || !reference) {
        throw new Error("Paystack payment metadata is incomplete");
      }

      const { default: PaystackPop } = await import("@paystack/inline-js");
      const paystack = new PaystackPop();

      setOpen(false);
      await paystack.checkout({
        key: process.env.NEXT_PUBLIC_PAYSTACK_PUBLIC_KEY || "",
        reference,
        email: paymentMeta?.contact_email || "",
        amount: Number(amount) * 100,
        currency: "NGN",
        metadata: paymentMeta,
        bearer: "subaccount",
        subaccountCode: paymentMeta?.sub_account_code,
        transactionCharge: Math.round(
          ((paymentMeta?.split_percentage ?? 0) / 100) *
            (Number(amount) * 100) || 0
        ),
        onSuccess: (transaction) => {
          onSuccessWithPaystack(transaction.reference || reference);
        },
        onError: (error) => {
          toast.error(error.message || "Unable to start Paystack checkout");
        },
      });
    } catch (error) {
      toast.error("Failed to initiate payment");
      console.error(error);
    }
  };
  const payWithFlutterwave: SubmitHandler<Values> = async (data) => {
    if (!eventUuid) {
      toast.error("Event ID is missing");
      return;
    }

    const input: GiftPaymentMetadataRequest = {
      amount: Number(data.amount),
      contact_email: data.email,
      contact_phone: phoneNumberValue,
      currency: currencyToPay?.toLowerCase() as Currency,
      currency_iso: currencyToPay?.toLowerCase() as Currency,
      event_id: `${eventUuid}`,
      full_name: data.fullName,
      note: "Cash gift payment",
      payment_provider: PaymentProcessor.Flutterwave,
      // user_id: profile?.user?.uuid ?? "",
    };

    try {
      // Call mutation to generate payment metadata
      const { data: mutationData } = await generateGiftMetadataFn({
        variables: {
          input,
        },
      });

      const paymentMeta = mutationData?.generateGiftPaymentMetadata;

      setFlutterwaveConfig({
        tx_ref: paymentMeta?.payment_reference || "",
        amount: Number(amount),
        public_key: process.env.NEXT_PUBLIC_FLUTTERWAVE_PUBLIC_KEY || "",
        currency: currencyToPay?.toLowerCase() as Currency,
        // meta: paymentMeta,
        meta: {
          inawo_product: `${paymentMeta?.inawo_product}`,
          event_id: `${paymentMeta?.event_id}`,
        },
        payment_options: "card,mobilemoney,ussd,banktransfer",
        customer: {
          email: paymentMeta?.contact_email || "",
          phone_number: phoneNumberValue,
          name: paymentMeta?.full_name || "",
        },
        customizations: {
          title: paymentMeta?.inawo_product || "",
          description: `Cash gift payment for ${paymentMeta?.inawo_product}`,
          logo: "",
        },
        subaccounts: [
          {
            id: `${paymentMeta?.sub_account_code}`,
            transaction_charge_type: "percentage",
            transaction_charge: (paymentMeta?.split_percentage ?? 0) / 100,
          },
        ],
      });
    } catch (error) {
      toast.error("Failed to initiate payment");
      console.error(error);
    }
  };

  const initializeFlutterWavePayment = useFlutterwave(
    flutterwaveConfig || {
      public_key: "",
      tx_ref: "",
      amount: 0,
      customer: {
        email: "",
        phone_number: "",
        name: "",
      },
      payment_options: "",
      customizations: {
        title: "",
        description: "",
        logo: "",
      },
    }
  );

  useEffect(() => {
    if (flutterwaveConfig) {
      setOpen(false);
      initializeFlutterWavePayment({
        callback: () => {
          console.log("callback");
          // verifyTransactionFn({
          //   variables: {
          //     reference: flutterwaveConfig?.tx_ref || "",
          //     provider: paymentProcessor.flutterwave,
          //   },
          // });
        },
        onClose: () => {
          console.log("onClose");
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flutterwaveConfig]);

  // const payWithMonnify: SubmitHandler<Values> = (data) => {
  //   setOpen(false);
  //   initializePaymentWithMonnify(onSuccessWithMonnify);
  // };

  const paystackNairaAmounts = [5000, 10000, 20000, 50000, 100000];
  const flutterwaveAmounts = [50, 100, 500, 1000, 2000];

  const finalAmount =
    provider === EventSupportCurrencyProvider.Paystack
      ? finalPaystackAmount
      : Number(amount);

  return (
    <div>
      <div className={cashGift["cashGift--content"]}>
        <div className={cashGift["cashGift--content__top"]}>
          <h1>Cash Gift ({currencyToPay?.toUpperCase()})</h1>
        </div>

        {isPreviewMode && (
          <p className="text-sm text-center text-[#71717A] mb-4">
            You are in preview mode.
          </p>
        )}

        <form className={cashGift["cashGift--content-form"]}>
          <div className={cashGift["cashGift--content-form_container"]}>
            <div className={cashGift["input"]}>
              <label htmlFor="fullName">Full Name</label>
              <input
                {...register("fullName")}
                type="text"
                name="fullName"
                placeholder="e.g Steve Bakery"
                className={cashGift["cashGift--content-form__field"]}
              />
            </div>
            <p className={cashGift["cashGift--content-form__errors"]}>
              {errors.fullName?.message} &nbsp;
            </p>
            <div className={cashGift["input"]}>
              <label htmlFor="email">Email</label>
              <input
                {...register("email")}
                type="text"
                className={cashGift["cashGift--content-form__field"]}
                name="email"
                placeholder="timi@gmail.com"
              />
            </div>
            <p className={cashGift["cashGift--content-form__errors"]}>
              {errors.email?.message}&nbsp;
            </p>
            <InputPhone
              name="phoneNumber"
              label="Phone Number"
              placeholder="Enter Phone Number (Whatsapp Number)"
              value={phoneNumberValue}
              setValue={setPhoneNumberValue}
            />
            <p className={cashGift["cashGift--content-form__errors"]}>&nbsp;</p>
            <div className={cashGift["input"]}>
              <label htmlFor="amount">Amount</label>
              <input
                {...register("amount")}
                type="number"
                name="amount"
                className={cashGift["cashGift--content-form__field"]}
                placeholder="Type the amount you want to gift"
              />
              <div className={cashGift["cashGift--content-form__note"]}>
                <span>
                  {amount
                    ? paystackCharge?.with100
                      ? "+1.5% + ₦100 processing fee"
                      : "+1.5% processing fee"
                    : ""}
                </span>
              </div>
            </div>
            <p className={cashGift["cashGift--content-form__errors"]}>
              {errors.amount?.message} &nbsp;
            </p>
          </div>

          <div className={cashGift["cashGift--content-form_container__group"]}>
            {provider === EventSupportCurrencyProvider.Flutterwave
              ? flutterwaveAmounts.map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => {
                      setValue("amount", amount, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    className={
                      cashGift["cashGift--content-form_container__group--btn"]
                    }
                  >
                    {getSymbolFromCurrency(currencyToPay as string)}{" "}
                    {formatMoney(amount)}
                  </button>
                ))
              : paystackNairaAmounts.map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => {
                      setValue("amount", amount, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    className={
                      cashGift["cashGift--content-form_container__group--btn"]
                    }
                  >
                    {getSymbolFromCurrency(currencyToPay as string)}{" "}
                    {formatMoney(amount)}
                  </button>
                ))}
          </div>

          <div className={cashGift["cashGift--content-btnContainer"]}>
            <Button
              type="button"
              design="secondary"
              label={
                provider === EventSupportCurrencyProvider.Paystack
                  ? "Pay via Paystack"
                  : "Pay via Flutterwave"
              }
              handleClick={
                provider === EventSupportCurrencyProvider.Paystack
                  ? handleSubmit(payWithPaystack)
                  : handleSubmit(payWithFlutterwave)
              }
              disabled={
                phoneNumberValue === "" || isSubmitting || !isDirty || !isValid
              }
            >
              <div>
                {provider === EventSupportCurrencyProvider.Paystack ? (
                  <Icon iconName="paystack" />
                ) : (
                  // <Icon iconName="flutterwave" />
                  <Icon iconName="flutterwave" />
                )}
                <p className={cashGift["cashGift--content-btnContainer-p"]}>
                  {phoneNumberValue === "" ||
                  isSubmitting ||
                  !isDirty ||
                  !isValid
                    ? `Pay ${
                        provider === EventSupportCurrencyProvider.Paystack
                          ? "via Paystack"
                          : provider ===
                            EventSupportCurrencyProvider.Flutterwave
                          ? "via Flutterwave"
                          : ""
                      }`
                    : `Pay ${getSymbolFromCurrency(
                        currencyToPay as string
                      )} ${finalAmount}`}
                </p>
              </div>
            </Button>

            {/* <Button
            type="button"
            design="secondary"
            label="Pay via Monnify"
            handleClick={handleSubmit(payWithMonnify)}
            disabled={
              phoneNumberValue === "" || isSubmitting || !isDirty || !isValid
            }
            >
            <div>
              <Icon iconName="monnify" />
              <p className={cashGift["cashGift--content-btnContainer-p"]}>
                Pay Via Monnify
              </p>
            </div>
          </Button> 
          */}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CashGiftPayment;
